export const ticketApiPath = (eid, segment) =>
  `/communicator/api/v1/endpoints/${eid}/tickets/${segment}`;

export class TicketApiError extends Error {
  constructor(status, code, description) {
    super(description || code || `Request failed with status ${status}`);
    this.name = 'TicketApiError';
    this.status = status;
    this.code = code;
    this.description = description;
  }
}

export const parseTicketError = async response => {
  try {
    const body = await response.json();

    return new TicketApiError(response.status, body?.code, body?.description);
  } catch {
    return new TicketApiError(response.status, undefined, response.statusText);
  }
};

const ticketFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  if (!response.ok) {
    throw await parseTicketError(response);
  }

  return response;
};

export const redeemSession = async (eid, code) => {
  const response = await ticketFetch(ticketApiPath(eid, 'sessions/redeem'), {
    method: 'POST',
    body: JSON.stringify({ code })
  });

  return response.json();
};

export const getTicketConfig = async eid => {
  const response = await ticketFetch(ticketApiPath(eid, 'config'), {
    method: 'GET'
  });

  return response.json();
};

export const submitTicketDetails = async (eid, body) => {
  const response = await ticketFetch(ticketApiPath(eid, 'details'), {
    method: 'POST',
    body: JSON.stringify(body)
  });

  return response.json();
};

export const uploadTicketAttachment = async (eid, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(ticketApiPath(eid, 'attachments'), {
    method: 'POST',
    credentials: 'include',
    // Exact File/Blob content length only — not multipart framing or request size.
    headers: { 'X-File-Size': String(file.size) },
    body: formData
  });

  if (!response.ok) {
    throw await parseTicketError(response);
  }

  return response.json();
};

export const uploadTicketAttachments = async (eid, files = []) => {
  if (!files.length) {
    return [];
  }

  const results = await Promise.allSettled(
    files.map(file => uploadTicketAttachment(eid, file))
  );

  const attachmentIds = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled' && result.value?.fileId) {
      attachmentIds.push(result.value.fileId);

      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      const file = files[index];
      const error = result.status === 'rejected' ? result.reason : null;
      // eslint-disable-next-line no-console
      console.warn('[TicketForm] attachment upload failed', file?.name, error);
    }
  });

  return attachmentIds;
};
